import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { home } from "@faire/retailer-visitor-shared/paths/index";
import { getSettingShowSignUpAnimation } from "@faire/retailer-visitor-shared/settings/getSettingShowSignUpAnimation";
import { SccContextStoreProvider } from "@faire/retailer-visitor-shared/views/scc-shared/SccContextStore";
import { useSccLimitIncreaseEligibility } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSccLimitIncreaseEligibility";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { AnnualSalesStep } from "@faire/retailer-visitor-shared/views/SignUp/annual-sales/AnnualSalesStep";
import { FaireDirectSurveyStep } from "@faire/retailer-visitor-shared/views/SignUp/brand-relationship-survey/FaireDirectSurveyStep";
import { BusinessTypeStep } from "@faire/retailer-visitor-shared/views/SignUp/business-type/BusinessTypeStep";
import { ConsumerStep } from "@faire/retailer-visitor-shared/views/SignUp/consumer/ConsumerStep";
import { LazySccDetailsStep } from "@faire/retailer-visitor-shared/views/SignUp/limit-increase-scc/LazySccDetailsStep";
import { LazySccNetTermsStep } from "@faire/retailer-visitor-shared/views/SignUp/limit-increase-scc/LazySccNetTermsStep";
import { LazySccTermsAgreementStep } from "@faire/retailer-visitor-shared/views/SignUp/limit-increase-scc/LazySccTermsAgreementStep";
import { NotifyMeStep } from "@faire/retailer-visitor-shared/views/SignUp/notify-me/NotifyMeStep";
import { NotifyMeSuccessStep } from "@faire/retailer-visitor-shared/views/SignUp/notify-me/success/NotifyMeSuccess";
import { OwFApplicationStep } from "@faire/retailer-visitor-shared/views/SignUp/open-with-faire/application/OwfApplicationStep";
import { OwFIntroStep } from "@faire/retailer-visitor-shared/views/SignUp/open-with-faire/intro/OwfIntroStep";
import { OpeningMonthStep } from "@faire/retailer-visitor-shared/views/SignUp/opening-month/OpeningMonthStep";
import { OpeningYearStep } from "@faire/retailer-visitor-shared/views/SignUp/opening-year/OpeningYearStep";
import { OwFGrantSocialMediaStep } from "@faire/retailer-visitor-shared/views/SignUp/owf-grant-social-media/OwFGrantSocialMediaStep";
import { PersonalInfoStep } from "@faire/retailer-visitor-shared/views/SignUp/personal/PersonalInfoStep";
import { ResellerCertStep } from "@faire/retailer-visitor-shared/views/SignUp/reseller-cert/ResellerCertStep";
import { SelectNetTermsStep } from "@faire/retailer-visitor-shared/views/SignUp/select-payment-terms/SelectNetTermsStep";
import { StoreInfoStep } from "@faire/retailer-visitor-shared/views/SignUp/store-info/StoreInfoStep";
import { StoreTypeStep } from "@faire/retailer-visitor-shared/views/SignUp/store-type-s1/StoreTypeStep";
import { SignUpLoadingPage } from "@faire/retailer-visitor-shared/views/SignUp/success/SignUpLoadingPage";
import { Switch } from "@faire/web--source/ui/routing/Switch";
import { Route } from "@faire/web-api--source/Route";
import brandPage from "@faire/web-api--source/routes/www/brand/brandToken";
import { props as annualSalesProps } from "@faire/web-api--source/routes/www/welcome/r/annual-sales";
import { props as faireDirectSignupSurveyProps } from "@faire/web-api--source/routes/www/welcome/r/brand-relationship-survey";
import { props as businessTypeProps } from "@faire/web-api--source/routes/www/welcome/r/business-type";
import { props as consumerRouteProps } from "@faire/web-api--source/routes/www/welcome/r/consumer";
import { props as notifyMeProps } from "@faire/web-api--source/routes/www/welcome/r/notify-me";
import { props as notifyMeSuccessProps } from "@faire/web-api--source/routes/www/welcome/r/notify-me/success";
import { props as openWithFaireApplicationRouteProps } from "@faire/web-api--source/routes/www/welcome/r/open-with-faire/application";
import { props as openWithFaireIntroRouteProps } from "@faire/web-api--source/routes/www/welcome/r/open-with-faire/intro";
import { props as openingMonthProps } from "@faire/web-api--source/routes/www/welcome/r/opening-month";
import { props as openingYearProps } from "@faire/web-api--source/routes/www/welcome/r/opening-year";
import personalInfo, {
  props as personalInfoRouteProps,
} from "@faire/web-api--source/routes/www/welcome/r/personal";
import { props as resellerCertProps } from "@faire/web-api--source/routes/www/welcome/r/reseller-cert";
import { props as sccDetailsRouteProps } from "@faire/web-api--source/routes/www/welcome/r/scc-details";
import { props as sccPaymentTermsRouteProps } from "@faire/web-api--source/routes/www/welcome/r/scc-payment-terms";
import { props as sccTermsAgreementRouteProps } from "@faire/web-api--source/routes/www/welcome/r/scc-terms-agreement";
import { props as selectTermsRouteProps } from "@faire/web-api--source/routes/www/welcome/r/select-payment-terms";
import { props as owfGrantSocialMediaProps } from "@faire/web-api--source/routes/www/welcome/r/social-media";
import { props as storeInfoProps } from "@faire/web-api--source/routes/www/welcome/r/store-info";
import { props as storeTypeS1Props } from "@faire/web-api--source/routes/www/welcome/r/store-type-s1";
import { props as successRouteProps } from "@faire/web-api--source/routes/www/welcome/r/success";
import React, { useMemo } from "react";
import { Redirect } from "react-router-dom";

export const SignUpRoutes: React.FC = () => {
  const { queryParams } = useSignUpQueryParams();

  const { isFaireDirectActiveInLoggedOut, faireDirectBrandToken } =
    useFaireDirectBrand();
  const isLoggedIn = useIsLoggedInRetailer();

  const { isTreatmentForSccLimitIncreaseExperiment } =
    useSccLimitIncreaseEligibility();

  const redirectUrl = useMemo(() => {
    if (isLoggedIn) {
      if (isFaireDirectActiveInLoggedOut) {
        return brandPage(faireDirectBrandToken);
      } else {
        return home();
      }
    }
    return personalInfo(queryParams);
  }, [
    isLoggedIn,
    queryParams,
    isFaireDirectActiveInLoggedOut,
    faireDirectBrandToken,
  ]);

  return (
    <Switch>
      <Route {...personalInfoRouteProps} component={PersonalInfoStep} />
      <Route
        {...faireDirectSignupSurveyProps}
        component={FaireDirectSurveyStep}
      />
      <Route {...selectTermsRouteProps} component={SelectNetTermsStep} />
      <Route {...consumerRouteProps} component={ConsumerStep} />
      <Route {...openWithFaireIntroRouteProps} component={OwFIntroStep} />
      <Route
        {...openWithFaireApplicationRouteProps}
        component={OwFApplicationStep}
      />
      {getSettingShowSignUpAnimation() ? (
        <Route {...successRouteProps} component={SignUpLoadingPage} />
      ) : null}
      <Route {...openingYearProps} component={OpeningYearStep} />
      <Route {...openingMonthProps} component={OpeningMonthStep} />
      <Route {...businessTypeProps} component={BusinessTypeStep} />
      <Route {...storeInfoProps} component={StoreInfoStep} />
      <Route {...annualSalesProps} component={AnnualSalesStep} />
      <Route {...resellerCertProps} component={ResellerCertStep} />
      <Route
        {...owfGrantSocialMediaProps}
        component={OwFGrantSocialMediaStep}
      />
      <Route {...storeTypeS1Props} component={StoreTypeStep} />
      <Route {...notifyMeProps} exact component={NotifyMeStep} />
      <Route {...notifyMeSuccessProps} component={NotifyMeSuccessStep} />
      <SccContextStoreProvider>
        {isTreatmentForSccLimitIncreaseExperiment ? (
          <Route
            {...sccTermsAgreementRouteProps}
            component={LazySccTermsAgreementStep}
          />
        ) : null}
        {isTreatmentForSccLimitIncreaseExperiment ? (
          <Route {...sccDetailsRouteProps} component={LazySccDetailsStep} />
        ) : null}
        {isTreatmentForSccLimitIncreaseExperiment ? (
          <Route
            {...sccPaymentTermsRouteProps}
            component={LazySccNetTermsStep}
          />
        ) : null}
      </SccContextStoreProvider>
      <Redirect to={redirectUrl} />
    </Switch>
  );
};
